import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  ExternalLink,
  Sparkles,
  Grid3X3,
  Link,
  Key,
  Database,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

import { AppListItem } from '@optidev/shared-types';

import { useAuth } from '@/contexts/AuthContext';
import { appsApi } from '@/lib/api/apps';
import { getActiveWorkspaceId } from '@/stores/workspace-store';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useResponsive } from '@/hooks/useResponsive';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { RemixAppModal } from '@/components/apps/remix-app-modal';
import { APP_CATEGORIES, getCategoryLabel, getCategoryIcon } from '@/components/apps/app-categories';

export default function Apps() {
  const { getIdToken } = useAuth();
  const navigate = useNavigate();
  const stickyBarRef = useRef<HTMLDivElement>(null);

  const [apps, setApps] = useState<AppListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedApp, setSelectedApp] = useState<AppListItem | null>(null);
  const [showRemixModal, setShowRemixModal] = useState(false);
  const [detailApp, setDetailApp] = useState<AppListItem | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  // Scroll to top when page mounts
  useEffect(() => {
    requestAnimationFrame(() => {
      const mainContainer = document.querySelector('main');
      if (mainContainer) {
        mainContainer.scrollTop = 0;
      }
      window.scrollTo(0, 0);
    });
  }, []);

  // Sticky bar scroll detection
  useEffect(() => {
    const mainContainer = document.querySelector('main');
    if (!mainContainer) return;

    const handleScroll = (): void => {
      setIsScrolled(mainContainer.scrollTop > 80);
    };

    mainContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => mainContainer.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (): Promise<void> => {
    try {
      const token = await getIdToken();
      if (!token) return;

      const appsData = await appsApi.getAll(token);
      setApps(appsData);
    } catch (error) {
      console.error('Failed to load apps:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter apps (flat list, no grouping)
  const filteredApps = useMemo(() => {
    let filtered = apps;

    if (searchQuery.trim()) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (app) =>
          app.name.toLowerCase().includes(lowerQuery) ||
          app.description?.toLowerCase().includes(lowerQuery) ||
          app.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((app) => app.categories.includes(selectedCategory));
    }

    return filtered;
  }, [apps, searchQuery, selectedCategory]);

  // Only show category pills that have at least one app
  const activeCategories = useMemo(() => {
    const usedCategories = new Set(apps.flatMap((app) => app.categories));
    return APP_CATEGORIES.filter((cat) => usedCategories.has(cat.value));
  }, [apps]);

  const handleRemixSuccess = useCallback((projectId: string) => {
    setShowRemixModal(false);
    setSelectedApp(null);
    const wsId = getActiveWorkspaceId();
    const params = new URLSearchParams();
    if (wsId) params.set('ws', wsId);
    params.set('from', 'app');
    navigate(`/dashboard/editor/${projectId}/power?${params.toString()}`);
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
        <div className="mb-5 sm:mb-10">
          {/* Mobile Header */}
          <div className="sm:hidden">
            <div className="mb-4">
              <h1 className="text-2xl font-bold text-foreground">App Library</h1>
              <p className="text-muted-foreground text-sm mt-1">
                Ready-made apps you can use in seconds
              </p>
            </div>

            {/* Mobile Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search apps..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-10"
              />
            </div>

            {/* Mobile Category Pills - explicit width constraint for scroll */}
            <div className="w-[calc(100vw-32px)] overflow-x-auto flex gap-2 pb-2">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium',
                    'border transition-colors duration-200 whitespace-nowrap flex-none',
                    selectedCategory === 'all'
                      ? 'bg-foreground/90 text-background border-foreground/90'
                      : 'bg-card text-muted-foreground border-border'
                  )}
                >
                  <Grid3X3 size={14} />
                  All
                </button>
                {activeCategories.map((cat) => {
                  const IconComponent = cat.icon;
                  return (
                    <button
                      key={cat.value}
                      onClick={() => setSelectedCategory(cat.value)}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium',
                        'border transition-colors duration-200 whitespace-nowrap flex-none',
                        selectedCategory === cat.value
                          ? 'bg-foreground/90 text-background border-foreground/90'
                          : 'bg-card text-muted-foreground border-border'
                      )}
                    >
                      <IconComponent size={14} />
                      {cat.label}
                    </button>
                  );
                })}
            </div>
          </div>

          {/* Desktop Header */}
          <div className="hidden sm:block">
            <div className="flex items-start justify-between gap-4">
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
                App Library
              </h1>
              <div className="relative w-[300px] shrink-0 mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search apps..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <p className="text-muted-foreground text-sm sm:text-base mt-2">
              Browse ready-made apps and start using them in seconds
            </p>
          </div>
        </div>

        {/* Sticky filter bar - Desktop only */}
        <div
          ref={stickyBarRef}
          className={cn(
            'hidden sm:flex sticky top-0 z-30 py-4',
            'items-center gap-4 transition-[border-color,box-shadow,background-color,backdrop-filter] duration-200',
            isScrolled
              ? 'border-b border-border shadow-sm bg-background/90 backdrop-blur-sm'
              : 'border-b border-transparent bg-transparent'
          )}
        >
          {/* Category Pills */}
          <div className="flex items-center gap-2 flex-1 min-w-0 overflow-x-auto">
            <button
              onClick={() => setSelectedCategory('all')}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium',
                'border transition-colors duration-200 whitespace-nowrap flex-none',
                selectedCategory === 'all'
                  ? 'bg-foreground/90 text-background border-foreground/90'
                  : 'bg-card text-muted-foreground border-border hover:text-foreground'
              )}
            >
              <Grid3X3 size={14} />
              All
            </button>

            {activeCategories.map((cat) => {
              const IconComponent = cat.icon;
              return (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium',
                    'border transition-colors duration-200 whitespace-nowrap flex-none',
                    selectedCategory === cat.value
                      ? 'bg-foreground/90 text-background border-foreground/90'
                      : 'bg-card text-muted-foreground border-border hover:text-foreground'
                  )}
                >
                  <IconComponent size={14} />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Mobile: Card list */}
        {filteredApps.length > 0 && (
          <div className="sm:hidden flex flex-col gap-3 mt-4">
            {filteredApps.map((app) => (
              <AppCard
                key={app.id}
                app={app}
                onClick={() => setDetailApp(app)}
                isMobile={true}
              />
            ))}
          </div>
        )}

        {/* Desktop: Grid view */}
        {filteredApps.length > 0 && (
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-6">
            {filteredApps.map((app) => (
              <AppCard
                key={app.id}
                app={app}
                onClick={() => setDetailApp(app)}
                isMobile={false}
              />
            ))}
          </div>
        )}

        {/* Empty state */}
        {apps.length === 0 && (
          <div className="text-center py-12 sm:py-20">
            <Grid3X3 size={40} className="sm:hidden mx-auto text-muted-foreground mb-4" />
            <Grid3X3 size={48} className="hidden sm:block mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-sm sm:text-base">No apps available yet.</p>
          </div>
        )}

        {/* No results */}
        {apps.length > 0 && filteredApps.length === 0 && (
          <div className="text-center py-12 sm:py-20">
            <Search size={40} className="sm:hidden mx-auto text-muted-foreground mb-4" />
            <Search size={48} className="hidden sm:block mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-sm sm:text-base">
              No apps found matching your search.
            </p>
          </div>
        )}
      </div>

      {/* App Detail Modal */}
      {detailApp && (
        <AppDetailModal
          app={detailApp}
          open={!!detailApp}
          onOpenChange={(open) => {
            if (!open) setDetailApp(null);
          }}
          onRemix={() => {
            setSelectedApp(detailApp);
            setDetailApp(null);
            setShowRemixModal(true);
          }}
        />
      )}

      {/* Remix Modal */}
      {selectedApp && (
        <RemixAppModal
          open={showRemixModal}
          onOpenChange={(open) => {
            setShowRemixModal(open);
            if (!open) setSelectedApp(null);
          }}
          app={selectedApp}
          onSuccess={handleRemixSuccess}
        />
      )}
    </div>
  );
}

// Strips markdown syntax to plain text for compact card previews
function stripMarkdown(text: string): string {
  return text
    .replace(/#{1,6}\s*/g, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`(.*?)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^[-*+]\s/gm, '')
    .replace(/^\d+\.\s/gm, '')
    .replace(/\n+/g, ' ')
    .trim();
}

// App Card Component - Slide-up lift on hover, click to open detail
interface AppCardProps {
  app: AppListItem;
  onClick: () => void;
  isMobile?: boolean;
}

function AppCard({ app, onClick, isMobile }: AppCardProps) {
  const firstCategory = app.categories[0];

  // Mobile: Horizontal card layout
  if (isMobile) {
    return (
      <div
        className="flex gap-3 p-3 bg-card rounded-xl border border-border/30 cursor-pointer overflow-hidden"
        onClick={onClick}
      >
        {/* Thumbnail - square on left */}
        <div className="relative w-20 h-20 flex-shrink-0 bg-muted rounded-lg overflow-hidden">
          {(app.customThumbnailUrl ?? app.thumbnailUrl) ? (
            <img
              src={app.customThumbnailUrl ?? app.thumbnailUrl}
              alt={`${app.name} preview`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
          ) : null}
          <div
            className={cn(
              'absolute inset-0 flex items-center justify-center bg-muted/50',
              (app.customThumbnailUrl ?? app.thumbnailUrl) ? 'hidden' : ''
            )}
          >
            <Sparkles size={20} className="text-muted-foreground/40" />
          </div>
        </div>
        {/* Content */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <h3 className="font-semibold text-base text-foreground truncate">
            {app.name}
          </h3>
          {app.tags && app.tags.length > 0 ? (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {app.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded"
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : firstCategory ? (
            <span className="inline-block mt-2 text-xs text-muted-foreground">
              {getCategoryLabel(firstCategory)}
            </span>
          ) : null}
        </div>
      </div>
    );
  }

  // Desktop: Vertical card layout
  return (
    <div
      className="group relative bg-card cursor-pointer overflow-hidden transition-colors border border-border rounded-lg hover:border-primary/30"
      onClick={onClick}
    >
      {/* Thumbnail */}
      <div className="aspect-[16/9] bg-muted relative overflow-hidden">
        {(app.customThumbnailUrl ?? app.thumbnailUrl) ? (
          <img
            src={app.customThumbnailUrl ?? app.thumbnailUrl}
            alt={`${app.name} preview`}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.nextElementSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        <div
          className={cn(
            'absolute inset-0 flex items-center justify-center',
            (app.customThumbnailUrl ?? app.thumbnailUrl) ? 'hidden' : ''
          )}
        >
          <Sparkles size={24} className="text-muted-foreground/30" />
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        <h3 className="font-medium text-sm text-foreground line-clamp-1 mb-1.5">
          {app.name}
        </h3>
        {app.categories.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {app.categories.map((cat) => (
              <span key={cat} className="text-[11px] text-muted-foreground bg-muted dark:bg-white/[0.06] px-1.5 py-0.5 rounded">
                {getCategoryLabel(cat)}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// App Detail Modal - Notion-style two-column popup
interface AppDetailModalProps {
  app: AppListItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRemix: () => void;
}

function AppDetailModal({ app, open, onOpenChange, onRemix }: AppDetailModalProps) {
  const { isMobile } = useResponsive();
  const hasRequirements =
    app.connections.length > 0 ||
    (app.config?.envVariables && app.config.envVariables.length > 0) ||
    app.config?.supabase?.required;

  // Shared content for both mobile and desktop
  const modalContent = (
    <>
      {/* Header */}
      <div className={cn(
        "flex items-center justify-between",
        isMobile ? "px-5 pt-2 pb-2" : "px-8 pt-8 pb-2"
      )}>
        {isMobile ? (
          <h2 className="text-xl font-bold tracking-tight">{app.name}</h2>
        ) : (
          <DialogTitle className="text-3xl font-bold tracking-tight">{app.name}</DialogTitle>
        )}
        <div className={cn(
          "flex items-center gap-2",
          isMobile ? "" : "gap-3 mr-8"
        )}>
          {app.previewUrl && (
            <a
              href={app.previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg border border-border font-medium hover:bg-accent transition-colors text-muted-foreground",
                isMobile ? "px-4 py-2 text-sm" : "px-5 py-2.5 text-sm"
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink size={isMobile ? 13 : 15} />
              {!isMobile && "Preview"}
            </a>
          )}
          <button
            onClick={onRemix}
            className={cn(
              "inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors font-medium",
              isMobile ? "px-4 py-2 text-sm" : "px-5 py-2.5 text-sm"
            )}
          >
            Use App
          </button>
        </div>
      </div>

      {/* Body - Single column on mobile, two columns on desktop */}
      <div className={cn(
        isMobile ? "flex flex-col px-5 pt-3 pb-6 space-y-5 overflow-y-auto" : "flex gap-8 px-8 pt-4 pb-8"
      )}>
        {/* Screenshot */}
        <div className={cn(isMobile ? "" : "flex-[3] min-w-0")}>
          <div className="rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.08),0_2px_8px_rgba(0,0,0,0.04)]">
            {(app.customThumbnailUrl ?? app.thumbnailUrl) ? (
              <img
                src={app.customThumbnailUrl ?? app.thumbnailUrl}
                alt={`${app.name} screenshot`}
                className="w-full h-auto object-cover"
              />
            ) : (
              <div className="aspect-video flex items-center justify-center bg-muted">
                <Sparkles size={32} className="text-muted-foreground/30" />
              </div>
            )}
          </div>
        </div>

        {/* Details */}
        <div className={cn(
          "space-y-5",
          isMobile ? "" : "flex-[2] min-w-[240px] space-y-7"
        )}>
          {/* Description - renders markdown */}
          {app.description && (
            <div>
              <h4 className={cn(
                "font-semibold text-foreground",
                isMobile ? "text-sm mb-1.5" : "text-base mb-2"
              )}>About this app</h4>
              <div className={cn(
                "text-muted-foreground leading-relaxed prose prose-neutral dark:prose-invert max-w-none [&_p]:mb-2 [&_ul]:mb-2 [&_ol]:mb-2 [&_li]:mb-0.5 [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-medium [&_a]:text-purple-600 [&_a]:underline",
                isMobile
                  ? "text-xs prose-xs [&_h1]:text-sm [&_h2]:text-xs [&_h3]:text-xs"
                  : "text-sm prose-sm [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm"
              )}>
                <ReactMarkdown>{app.description}</ReactMarkdown>
              </div>
            </div>
          )}

          {/* What you'll need */}
          {hasRequirements && (
            <div>
              <h4 className={cn(
                "font-semibold text-foreground",
                isMobile ? "text-sm mb-2" : "text-base mb-3"
              )}>What you'll need</h4>
              <div className={cn(isMobile ? "space-y-2" : "space-y-2.5")}>
                {app.connections.length > 0 && app.connections.map((conn) => (
                  <div key={conn.key} className={cn(
                    "flex items-center gap-2 text-muted-foreground",
                    isMobile ? "text-xs" : "text-sm gap-2.5"
                  )}>
                    <Link size={isMobile ? 13 : 15} className="shrink-0" />
                    <span>{conn.label}</span>
                  </div>
                ))}
                {app.config?.envVariables && app.config.envVariables.length > 0 && (
                  <div className={cn(
                    "flex items-center gap-2 text-muted-foreground",
                    isMobile ? "text-xs" : "text-sm gap-2.5"
                  )}>
                    <Key size={isMobile ? 13 : 15} className="shrink-0" />
                    <span>{app.config.envVariables.length} API key{app.config.envVariables.length > 1 ? 's' : ''}</span>
                  </div>
                )}
                {app.config?.supabase?.required && (
                  <div className={cn(
                    "flex items-center gap-2 text-muted-foreground",
                    isMobile ? "text-xs" : "text-sm gap-2.5"
                  )}>
                    <Database size={isMobile ? 13 : 15} className="shrink-0" />
                    <span>OptiDev Cloud (set up automatically)</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Categories */}
          {app.categories.length > 0 && (
            <div>
              <h4 className={cn(
                "font-semibold text-foreground",
                isMobile ? "text-sm mb-2" : "text-base mb-3"
              )}>Categories</h4>
              <div className="flex flex-wrap gap-2">
                {app.categories.map((cat) => {
                  const IconComponent = getCategoryIcon(cat);
                  return (
                    <span
                      key={cat}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-lg text-muted-foreground border border-border",
                        isMobile ? "px-2.5 py-0.5 text-xs" : "px-3 py-1 text-sm"
                      )}
                    >
                      <IconComponent size={isMobile ? 11 : 13} />
                      {getCategoryLabel(cat)}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  // Mobile: Use BottomSheet for proper slide-up behavior
  if (isMobile) {
    return (
      <BottomSheet open={open} onOpenChange={onOpenChange} hideTitle>
        <div className="flex flex-col h-full overflow-hidden">
          {modalContent}
        </div>
      </BottomSheet>
    );
  }

  // Desktop: Use Dialog
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1100px] max-h-[85vh] p-0 gap-0 overflow-y-auto">
        {modalContent}
      </DialogContent>
    </Dialog>
  );
}
